import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
  IconButton,
} from '@c2fo/liquidity';
import { DownloadIcon } from '@c2fo/liquidity/icons';

export interface DownloadMenuGroup<Group extends string, Format extends string> {
  id: Group;
  label: string;
  /** A format with `href` renders as a link, so the browser handles the download; `onSelect` still fires. */
  formats: { id: Format; label: string; href?: string }[];
}

interface DownloadMenuProps<Group extends string, Format extends string> {
  /** Accessible name for the icon trigger. */
  name: string;
  groups: DownloadMenuGroup<Group, Format>[];
  onSelect: (group: Group, format: Format) => void;
  disabled?: boolean;
}

/**
 * A download icon that opens a menu of file groups, each listing the formats it can be downloaded in.
 */
const DownloadMenu = <Group extends string, Format extends string>({
  name,
  groups,
  onSelect,
  disabled,
}: DownloadMenuProps<Group, Format>) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <IconButton
        icon={DownloadIcon}
        name={name}
        size="sm"
        variant="ancillary"
        disabled={disabled}
        className="[&>svg]:size-5"
      />
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end" className="min-w-48" collisionPadding={24}>
      {groups.map((group) => (
        <DropdownMenuGroup key={group.id} aria-label={group.label}>
          <DropdownMenuLabel>{group.label}</DropdownMenuLabel>
          {group.formats.map((format) =>
            format.href ? (
              <DropdownMenuItem key={format.id} asChild>
                <a href={format.href} target="_self" onClick={() => onSelect(group.id, format.id)}>
                  {format.label}
                </a>
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem key={format.id} onClick={() => onSelect(group.id, format.id)}>
                {format.label}
              </DropdownMenuItem>
            )
          )}
        </DropdownMenuGroup>
      ))}
    </DropdownMenuContent>
  </DropdownMenu>
);

export default DownloadMenu;
